
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Navbar from '../../components/feature/Navbar';
import { useAuth } from '@/hooks/useAuth';
import { blogsService } from '../../services/blogs';
import { uploadService } from '../../services/upload';

export default function CreateBlog() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'Blog',
    tags: '',
    coverImage: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const quillRef = useRef<ReactQuill>(null);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        const response = await uploadService.uploadImage(file);
        if (response.success) {
          const quill = quillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, 'image', response.data.url, 'user');
            quill.setSelection(range.index + 1, 0);
          }
        }
      } catch (err) {
        console.error('Error subiendo imagen al editor:', err);
      }
    };
  }, []);

  // Estilos personalizados para ReactQuill
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
  }), [imageHandler]);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  // Cargar datos si es edición
  useEffect(() => {
    if (id && user) {
      loadPost();
    }
  }, [id, user]);

  const loadPost = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const post = await blogsService.getBlogPost(id);
      if (post) {
        if (user && post.author_id !== user.id) {
          alert('No tienes permiso para editar este artículo');
          navigate('/');
          return;
        }
        setFormData({
          title: post.title,
          excerpt: post.excerpt,
          content: post.content,
          category: post.category,
          tags: post.tags.join(', '),
          coverImage: post.cover_image || ''
        });
      } else {
        setError('Artículo no encontrado');
      }
    } catch (err) {
      console.error(err);
      setError('Error al cargar el artículo');
    } finally {
      setLoading(false);
    }
  };

  // Verificar autenticación
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-lp-bg">
        <Navbar />
        <div className="pt-20 px-4">
          <div className="vintage-paper aged-paper p-6 text-center shadow-lg">
            <i className="ri-lock-line text-3xl text-sepia-400 mb-3"></i>
            <h2 className="font-headings text-lg text-sepia-900 mb-2">
              Acceso Restringido
            </h2>
            <p className="text-sm text-sepia-700 font-body mb-4">
              Necesitas iniciar sesión para crear contenido
            </p>
            <button 
              onClick={() => navigate('/')}
              className="vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-4 py-2 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleContentChange = (content: string) => {
    setFormData(prev => ({ ...prev, content }));
    setError('');
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setError('Solo se permiten archivos de imagen');
      return;
    }

    // Validar tamaño (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no debe superar los 5MB');
      return;
    }

    setUploadingImage(true);
    setError('');

    try {
      const response = await uploadService.uploadImage(file);
      if (response.success) {
        setFormData(prev => ({ 
          ...prev, 
          coverImage: response.data.url 
        }));
      } else {
        setError(response.message || 'Error al subir la imagen');
      }
    } catch (err: any) {
      setError(err.message || 'Error al subir la imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const validateForm = () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('El título y contenido son obligatorios');
      return false;
    }

    if (!user) {
      setError('Error de autenticación');
      return false;
    }

    return true;
  };

  const buildBlogPostData = () => ({
    title: formData.title,
    excerpt: formData.excerpt || formData.content.substring(0, 150) + '...',
    content: formData.content,
    category: formData.category,
    tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
    cover_image: formData.coverImage
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !user) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const blogPostData = buildBlogPostData();

      if (id) {
        await blogsService.updateBlogPost(id, blogPostData);
        alert('Artículo actualizado correctamente');
      } else {
        const newBlogPost = {
          ...blogPostData,
          author_id: user.id,
          author_name: user.fullName,
          is_affiliated: user.isAffiliated,
          is_published: user.isAffiliated
        };
        await blogsService.createBlogPost(newBlogPost);
        
        const message = user.isAffiliated 
          ? '¡Artículo publicado exitosamente!' 
          : '¡Artículo enviado para revisión! Será publicado una vez aprobado.';
        alert(message);
      }

      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Error al publicar el artículo. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!validateForm() || !user) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const blogPostData = buildBlogPostData();

      if (id) {
        await blogsService.updateBlogPost(id, { ...blogPostData, is_published: false });
        alert('Borrador actualizado correctamente');
      } else {
        const newBlogPost = {
          ...blogPostData,
          author_id: user.id,
          author_name: user.fullName,
          is_affiliated: user.isAffiliated,
          is_published: false
        };
        await blogsService.createBlogPost(newBlogPost);
        alert('Borrador guardado correctamente');
      }

      navigate('/profile');
    } catch (err: any) {
      setError(err.message || 'Error al guardar el borrador. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-lp-bg">
      <Navbar />
      
      <div className="pt-20 px-4 pb-8">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sepia-600 hover:text-sepia-800 font-ui text-sm mb-4 transition-colors duration-200"
          >
            <i className="ri-arrow-left-line"></i>
            Volver
          </button>
          
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-sepia-200/60 rounded-full flex items-center justify-center">
                <i className="ri-user-line text-sepia-700"></i>
              </div>
              <div>
                <h1 className="font-headings text-xl text-sepia-900">CREAR ARTÍCULO</h1>
                <p className="text-sm text-sepia-600 font-ui">Bienvenido, {user?.fullName}</p>
              </div>
            </div>
            
            {!user?.isAffiliated && (
              <div className="vintage-paper-subtle p-3 rounded-[10%] border-l-4 border-amber-400">
                <p className="text-sm text-amber-700 font-ui">
                  <i className="ri-information-line mr-2"></i>
                  Tu contenido será revisado antes de publicarse. Considera afiliarte para publicación inmediata.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <label className="block text-sm font-ui text-sepia-700 mb-3">
              Título del artículo *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="w-full p-4 text-lg font-headings text-sepia-800 bg-sepia-50/50 border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-400/50"
              placeholder="Escribe un título atractivo para tu artículo..."
              maxLength={100}
            />
            <div className="flex justify-end mt-2">
              <span className="text-xs text-sepia-500 font-ui">
                {formData.title.length}/100 caracteres
              </span>
            </div>
          </div>

          {/* Cover Image */}
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <label className="block text-sm font-ui text-sepia-700 mb-3">
              Imagen de portada
            </label>
            
            <div className="space-y-4">
              {/* Preview */}
              {formData.coverImage && (
                <div className="relative group">
                  <img 
                    src={formData.coverImage} 
                    alt="Vista previa de portada" 
                    className="w-full h-48 object-cover rounded-[10%] border-2 border-sepia-300/50"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, coverImage: '' }))}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              )}

              {/* Upload Button */}
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  className="hidden"
                  id="cover-image-upload"
                />
                <label
                  htmlFor="cover-image-upload"
                  className={`flex items-center justify-center gap-3 w-full p-6 border-2 border-dashed rounded-[10%] cursor-pointer transition-all duration-200 ${
                    uploadingImage 
                      ? 'border-gray-300 bg-gray-50 cursor-not-allowed' 
                      : 'border-sepia-300/50 bg-sepia-50/30 hover:border-sepia-400 hover:bg-sepia-50/50'
                  }`}
                >
                  {uploadingImage ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-2xl text-sepia-600"></i>
                      <span className="text-sm text-sepia-600 font-ui">Subiendo imagen...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-image-add-line text-2xl text-sepia-600"></i>
                      <div className="text-center">
                        <span className="text-sm text-sepia-700 font-ui block">
                          Click para subir imagen
                        </span>
                        <span className="text-xs text-sepia-500 font-ui">
                          JPG, PNG, GIF (Máx. 5MB)
                        </span>
                      </div>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* Category and Tags */}
          <div className="vintage-paper aged-paper p-6 shadow-lg space-y-4">
            <div>
              <label className="block text-sm font-ui text-sepia-700 mb-3">
                Categoría
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full p-3 text-sm font-body text-sepia-800 bg-sepia-50/50 border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-400/50"
              >
                <option value="Blog">Blog</option>
                <option value="Literatura">Literatura</option>
                <option value="Poesía">Poesía</option>
                <option value="Ensayo">Ensayo</option>
                <option value="Teatro">Teatro</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-ui text-sepia-700 mb-3">
                Etiquetas (separadas por comas)
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                className="w-full p-3 text-sm font-body text-sepia-800 bg-sepia-50/50 border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-400/50"
                placeholder="literatura, análisis, reseña..."
              />
            </div>
          </div>

          {/* Excerpt */}
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <label className="block text-sm font-ui text-sepia-700 mb-3">
              Resumen (opcional)
            </label>
            <textarea
              name="excerpt"
              value={formData.excerpt}
              onChange={handleInputChange}
              rows={3}
              className="w-full p-3 text-sm font-body text-sepia-800 bg-sepia-50/50 border border-sepia-300/50 rounded-[10%] resize-none focus:outline-none focus:ring-2 focus:ring-sepia-400/50"
              placeholder="Breve descripción de tu artículo..."
              maxLength={200}
            />
            <div className="flex justify-end mt-2">
              <span className="text-xs text-sepia-500 font-ui">
                {formData.excerpt.length}/200 caracteres
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <label className="block text-sm font-ui text-sepia-700 mb-3">
              Contenido del artículo *
            </label>
            <div className="bg-sepia-50/50 rounded-[10%] overflow-hidden border border-sepia-300/50">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={formData.content}
                onChange={handleContentChange}
                modules={quillModules}
                formats={quillFormats}
                className="font-body text-sepia-800"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="vintage-paper-subtle p-4 rounded-[10%] border-l-4 border-red-400">
              <p className="text-sm text-red-700 font-ui">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex-1 vintage-paper-subtle text-sepia-700 hover:bg-sepia-100/70 px-4 py-3 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md border border-sepia-300/50"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={handleSaveDraft}
                className="flex-1 vintage-paper-subtle text-sepia-900 hover:bg-sepia-100/70 px-4 py-3 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-sepia-300/50"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Guardando...
                  </>
                ) : (
                  <>
                    <i className="ri-draft-line"></i>
                    Guardar borrador
                  </>
                )}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-4 py-3 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Guardando...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    {id ? 'Guardar Cambios' : (user?.isAffiliated ? 'Publicar' : 'Enviar para revisión')}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Vintage Background */}
        <div 
          className="fixed inset-0 pointer-events-none opacity-5 z-0"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=vintage%20writing%20desk%20scene%2C%20antique%20typewriter%2C%20fountain%20pen%2C%20ink%20bottles%2C%20old%20manuscripts%2C%20aged%20paper%20sheets%2C%20warm%20sepia%20lighting%2C%20literary%20workspace%20atmosphere%2C%20wooden%20desk%20with%20brass%20details%2C%20classic%20scholarly%20setting%2C%20nostalgic%20ambiance%2C%20brown%20and%20cream%20tones&width=375&height=812&seq=create_blog_bg&orientation=portrait')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'multiply'
          }}
        />
      </div>
    </div>
  );
}
